import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { useController } from 'react-hook-form';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { SettingsComponent } from '../../lib/declarations/EditorComponentTypes';
import ActionMenu from '../../../components/ActionMenu';
import { useCurrentBlock } from '../CurrentBlockProvider';
import { Alignment, PositionAlignment } from '../../../declarations/models/Alignment';
import { BlockSpecificPage } from '../../lib/declarations/BlockSpecificPage';
import { BaseBlock } from '../../../declarations/models/blocks/BaseBlock';

const ALIGNMENT_OPTIONS: Array<PositionAlignment> = [
  Alignment.NORMAL,
  Alignment.LEFT,
  Alignment.RIGHT,
  Alignment.WIDE,
  Alignment.FULL,
];
const ALIGNMENT_ICONS: Record<PositionAlignment, ReactNode> = {
  [Alignment.NORMAL]: <MaterialSymbol name='width_normal' />,
  [Alignment.LEFT]: <MaterialSymbol name='format_align_left' />,
  [Alignment.RIGHT]: <MaterialSymbol name='format_align_right' />,
  [Alignment.WIDE]: <MaterialSymbol name='width_wide' />,
  [Alignment.FULL]: <MaterialSymbol name='width_full' />,
};

export const PageBlockPositionSetting: SettingsComponent = () => {
  const { t: tAria } = useTranslation('aria');
  const { t: tComponents } = useTranslation('components');
  const { blockPath } = useCurrentBlock();
  const {
    field: { value, onChange },
  } = useController<BlockSpecificPage<BaseBlock & { position?: PositionAlignment }>, `${typeof blockPath}.position`>({
    name: `${blockPath}.position`,
  });

  return (
    <ActionMenu
      id='alignment-selector'
      ariaLabel={tAria('components.PageBlockPositionSetting.ActionMenuLabel')}
      tooltip={tComponents('PageBlockPositionSetting.Tooltip')}
      tooltipPlacement='top'
      icon={value ? ALIGNMENT_ICONS[value] : ALIGNMENT_ICONS.normal}>
      {ALIGNMENT_OPTIONS.map((alignmentOption) => (
        <MenuItem key={alignmentOption} onClick={() => onChange(alignmentOption)} selected={value === alignmentOption}>
          <ListItemIcon>{ALIGNMENT_ICONS[alignmentOption]}</ListItemIcon>
          <ListItemText>{tComponents(`PageBlockPositionSetting.options.${alignmentOption}`)}</ListItemText>
        </MenuItem>
      ))}
    </ActionMenu>
  );
};

export default PageBlockPositionSetting;
